import { FORM_ERROR, getDRFFormError, type SubmissionErrors } from '@ff-it/form';
import { Page, ItemLoader, NotFound, useActivities, useEntity, useModel, type EntityContextType } from 'components';
import { useHasPerm } from 'core/permissions';
import type { EmbeddedProduct } from 'modules/supplier/products';
import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router';
import { api } from 'services';
import type { ActivityType } from 'types';
import { activityTypes } from 'types';
import { formatError } from 'utilities';
import { ProvidedScopeEditor, type ProvidedScopeValues } from './providedScope';
import { ProviderAgreementDiscountEditor, buildTree, pathFromProduct, targetOptions } from './providerDiscounts';
import type {
  ProviderAgreementDiscountRule,
  ProviderAgreementDiscountRuleInput,
  ProviderAgreementPartitionRecord,
} from './providerDiscounts/types';
import type { ProviderAgreement } from './types';
import * as styles from './ProviderAgreementPartition.css';

export function ProviderAgreementPartition(): ReactElement {
  const { type } = useParams<{ type: ActivityType }>();
  if (!type || !activityTypes.includes(type)) return <NotFound />;
  return <PartitionLoaders type={type} />;
}

function PartitionLoaders({ type }: { type: ActivityType }): ReactElement {
  const { endpoint } = useEntity<ProviderAgreement>();
  const partitionUrl = `${endpoint}types/${type}/`;
  return (
    <ItemLoader<ProviderAgreementPartitionRecord> url={partitionUrl}>
      {(partitionContext) => (
        <ItemLoader<EmbeddedProduct[]> url={`${partitionUrl}products/`}>
          {(productsContext) => (
            <PartitionWorkspace
              type={type}
              partitionUrl={partitionUrl}
              partitionContext={partitionContext}
              productsContext={productsContext}
            />
          )}
        </ItemLoader>
      )}
    </ItemLoader>
  );
}

function PartitionWorkspace({
  type,
  partitionUrl,
  partitionContext,
  productsContext,
}: {
  type: ActivityType;
  partitionUrl: string;
  partitionContext: EntityContextType<ProviderAgreementPartitionRecord>;
  productsContext: EntityContextType<EmbeddedProduct[]>;
}): ReactElement {
  const { permissions } = useModel();
  const agreement = useEntity<ProviderAgreement>();
  const canChange = useHasPerm(permissions?.change);
  const allActivities = useActivities();
  const [scopeDirty, setScopeDirty] = useState(false);
  const { item: partition, mutate: mutatePartition } = partitionContext;
  const { item: products, mutate: mutateProducts } = productsContext;

  const rules = partition.discount_rates;
  const scopePaths = products.map(pathFromProduct);
  const nodes = buildTree(rules);
  const choices = targetOptions(scopePaths);
  const activities = allActivities.filter((activity) => activity.kind === 'MEDIA');

  const refreshAgreement = () => agreement.mutate();
  const saveScope = async (scope: ProvidedScopeValues): Promise<SubmissionErrors | void> => {
    const response = await api.request<ProviderAgreementPartitionRecord, unknown>({
      method: 'PUT',
      url: `${partitionUrl}provided/`,
      body: scope,
    });
    if (!response.ok) {
      return (
        getDRFFormError(response) ?? {
          [FORM_ERROR]: formatError(response) || 'Unable to save provided scope.',
        }
      );
    }
    await mutatePartition(response.data, { revalidate: false });
    await Promise.all([mutateProducts(), refreshAgreement()]);
  };

  const saveRule = async (rule: ProviderAgreementDiscountRuleInput, ruleId?: number) => {
    const response = await api.request<ProviderAgreementDiscountRule, unknown>({
      method: ruleId !== undefined ? 'PUT' : 'POST',
      url: ruleId !== undefined ? `${partitionUrl}discount-rates/${ruleId}/` : `${partitionUrl}discount-rates/`,
      body: rule,
    });
    if (!response.ok) throw response;
    // The response is one rule, so revalidate the partition to receive the
    // server's canonical target-path ordering rather than appending it to a
    // locally reconstructed order.
    await mutatePartition();
    await refreshAgreement();
  };

  const removeRule = async (ruleId: number) => {
    const response = await api.request<unknown, unknown>({
      method: 'DELETE',
      url: `${partitionUrl}discount-rates/${ruleId}/`,
    });
    if (!response.ok) throw response;
    await mutatePartition(
      (current) =>
        current ? { ...current, discount_rates: current.discount_rates.filter((rate) => rate.id !== ruleId) } : current,
      { revalidate: false },
    );
    await refreshAgreement();
  };

  const reorderRules = async (
    targetPath: ProviderAgreementDiscountRuleInput['target_path'],
    rateIds: readonly number[],
  ) => {
    const response = await api.request<ProviderAgreementDiscountRule[], unknown>({
      method: 'POST',
      url: `${partitionUrl}discount-rates/reorder/`,
      body: { target_path: targetPath, rate_ids: rateIds },
    });
    if (!response.ok) throw response;
    await mutatePartition();
  };

  return (
    <Page>
      <div className={styles.workspace}>
        <ProvidedScopeEditor
          activeType={type}
          vendors={partition.provided_vendors.map((link) => link.vendor)}
          serviceCategories={partition.provided_service_categories.map((link) => link.service_category)}
          onSave={saveScope}
          onDirtyChange={setScopeDirty}
          readOnly={!canChange}
        />
        <ProviderAgreementDiscountEditor
          activeType={type}
          nodes={nodes}
          scopePaths={scopePaths}
          targetOptions={choices}
          activities={activities}
          onAddRule={saveRule}
          onEditRule={saveRule}
          onRemoveRule={removeRule}
          onReorderRules={reorderRules}
          readOnly={!canChange}
          disabled={scopeDirty}
        />
      </div>
    </Page>
  );
}
